import Spinner from '$components/Spinner/Spinner';
import { useGetAdditionalDocumentsByCandidateIdQuery } from '$store/modules/additional-documents';
import { userLanguage } from '$translations/index';
import { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { Worker, Viewer } from '@react-pdf-viewer/core';
import { zoomPlugin } from '@react-pdf-viewer/zoom';
import { getFilePlugin } from '@react-pdf-viewer/get-file';
import { useTranslation } from 'react-i18next';
import { ReactComponent as SimeraDark } from '$icons/simera-dark logo.svg';
/* Viewer component css */
import '@react-pdf-viewer/core/lib/styles/index.css';
/* react-pdf-viewer Zoom css */
import '@react-pdf-viewer/zoom/lib/styles/index.css';
import { useGetCvQuery } from '$store/modules/uploads';

const PreviewPDF = ({
  pdfFileUrl,
  hideBanner,
}: {
  pdfFileUrl?: string;
  hideBanner?: boolean;
}) => {
  const { fileId, candidateId, type } = useParams();
  const [t] = useTranslation('global');
  const [pdfUrl, setPdfUrl] = useState(pdfFileUrl || '');
  const zoomPluginInstance = zoomPlugin();
  const { ZoomInButton, ZoomPopover, ZoomOutButton } = zoomPluginInstance;
  const getFilePluginInstance = getFilePlugin();
  const { Download } = getFilePluginInstance;
  const { isLoading: isLoadingFile, data: fileData } =
    useGetAdditionalDocumentsByCandidateIdQuery(
      {
        candidateId: parseInt(candidateId as string, 10),
        lang: userLanguage,
      },
      { skip: type !== 'file' || !fileId || !candidateId || !!pdfFileUrl }
    );
  const { isLoading: isLoadingCV, data: cvData } = useGetCvQuery(
    {
      candidateId: parseInt(candidateId as string, 10),
    },
    { skip: !candidateId || type !== 'cv' || !!pdfFileUrl }
  );

  const renderError = () => {
    return (
      <div className="justify-center flex min-h-full">
        {t('modalDocumentPdf.error')}
      </div>
    );
  };

  useEffect(() => {
    let matchedPdfUrl;

    if (type === 'file' && fileData)
      matchedPdfUrl = fileData?.find(
        ({ Id }) => Id === parseInt(fileId as string, 10)
      )?.Url;

    if (type === 'cv' && cvData) matchedPdfUrl = cvData?.Url;

    if (matchedPdfUrl) {
      setPdfUrl(matchedPdfUrl);
    }
  }, [cvData, fileData, fileId, setPdfUrl, type]);

  return (
    <div className="min-h-screen">
      {!hideBanner && (
        <div className="flex justify-between items-center bg-white h-14 px-4 md:px-8">
          <SimeraDark className=" h-10" />
        </div>
      )}

      <div className="flex flex-1">
        {isLoadingFile || isLoadingCV ? (
          <Spinner />
        ) : (
          <div className={`flex flex-col w-full ${!hideBanner && 'px-8'}`}>
            <Worker workerUrl="https://unpkg.com/pdfjs-dist@3.4.120/build/pdf.worker.min.js">
              {!pdfUrl ? (
                renderError()
              ) : (
                <div className="flex flex-col justify-center w-full items-center relative">
                  <div className="flex flex-row gap-4 sticky z-50 top-4">
                    <div className="flex flex-row rounded-full bg-gray-200 shadow overflow-hidden gap-4">
                      <ZoomOutButton />
                      <ZoomPopover />
                      <ZoomInButton />
                    </div>
                    <div className="flex flex-row rounded-full bg-gray-200 shadow overflow-hidden">
                      <Download />
                    </div>
                  </div>
                  <Viewer
                    fileUrl={pdfUrl}
                    plugins={[zoomPluginInstance, getFilePluginInstance]}
                  />
                </div>
              )}
            </Worker>
          </div>
        )}
      </div>
    </div>
  );
};

PreviewPDF.defaultProps = {
  pdfFileUrl: undefined,
  hideBanner: false,
};

export default PreviewPDF;
